/* ═════
   PDP · filtro de la galería por variante + dots
   Decisión: obsidian-vault/_decisions/2026-09-08-galeria-pdp-filtro-por-variante.md

   Regla de tres baldes:
     1. media de la variante elegida   → se muestra
     2. media SIN variante (lifestyle) → siempre se muestra
     3. media de otra variante         → se oculta

   Escalera de fallback, de más específico a más permisivo:
     a) variante CON imagen  → su foto + las compartidas
     b) variante SIN imagen  → solo las compartidas. Son de ambiente y
        detalle, sirven para cualquier variante; mostrar las de OTRAS
        variantes sería ruido, que es justo lo que estamos sacando.
     c) si a) o b) dejan menos de MIN_VISIBLE → no se filtra nada y se
        muestran todas, como antes. Cubre el producto que tiene todas
        las fotos etiquetadas y ninguna compartida.

   POR QUÉ NO ALCANZA CON CSS: la clase .is-current-variant la escribe
   el Liquid una sola vez, en el render (media-gallery.liquid:121). El
   JS del theme la LEE (media-gallery.js:58 y :322) pero nunca la mueve,
   así que una regla CSS colgada de esa clase dejaría la galería
   congelada en la variante con la que cargó la página.

   Se engancha a 'on:variant:change', que es el mismo evento que ya usa
   el theme (media-gallery.js:77) y que dispara variant-picker.js:675.
   No pisa nada del theme: solo agrega y saca una clase propia.

   Después de filtrar llama a gallery.setVisibleItems(), que es un método
   público del custom element (media-gallery.js:613). Ese método arma
   this.visibleItems filtrando por clientWidth > 0 y escribe el total del
   contador, así que el "1 of 54" pasa a decir el número real sin que
   tengamos que tocarlo.
   ═════ */
(() => {
  const MIN_VISIBLE = 3;
  const HIDDEN = 'rs-media--other-variant';

  // La regla vive acá y no en product-page.css a propósito: la clase solo
  // existe porque este script la pone, así que si el archivo no carga no
  // queda una regla huérfana capaz de ocultar fotos sin nadie que las
  // vuelva a mostrar. Un hogar, sin posibilidad de que se desincronicen.
  const inyectarEstilo = () => {
    if (document.getElementById('rs-pdp-gallery-style')) return;
    const st = document.createElement('style');
    st.id = 'rs-pdp-gallery-style';
    st.textContent = [
      // SOLO MOBILE, por decisión de Abi (2026-09-08). El JS pone la clase
      // en todos los breakpoints, pero ocultar es cosa del CSS y solo
      // ocurre debajo de 769. Así no hace falta lógica de resize: cruzar
      // el breakpoint no requiere recalcular nada.
      // Consecuencia: en desktop la galería y los thumbs siguen mostrando
      // las 54 fotos. Para extenderlo a desktop alcanza con sacar este
      // @media.
      '@media (max-width:768px){',
      '.media-viewer__item.' + HIDDEN + ',',
      '.media-thumbs__item.' + HIDDEN + '{display:none}}',
      // Ventana de 5: el riel se traslada y los dots del borde se achican.
      // 5 dots de 6 + 4 gaps de 8 = 62.
      '.rs-dots{display:flex;justify-content:center;margin-top:16px}',
      '.rs-dots__win{overflow:hidden;width:62px;height:8px}',
      '.rs-dots__rail{display:flex;gap:8px;align-items:center;height:8px;',
      'transition:transform .25s ease}',
      '.rs-dots__dot{flex:0 0 6px;width:6px;height:6px;border-radius:3px;',
      'background:#C9C0B1;transition:transform .2s ease,background-color .2s ease}',
      // El activo crece por TRANSFORM, no por height. Antes tenía
      // height:8px con width:6px y quedaba un óvalo de 6×8 (Abi lo vio).
      // Salía de leer el Figma literal, donde el botón activo mide 6×8
      // adentro de un contenedor de 6×6 — un resize accidental del archivo.
      // scale() no afecta al layout, así que el pitch sigue siendo 14 y la
      // matemática de la ventana no se toca. 6 × 1.33 = 8.
      '.rs-dots__dot--on{background:#1A1816;transform:scale(1.33)}',
      // :not() para que nunca compitan dos transforms sobre el mismo dot.
      // Hoy no llegan a coincidir (el activo va al centro de la ventana y
      // los bordes a las puntas), pero no depende de eso.
      '.rs-dots__dot--edge:not(.rs-dots__dot--on){transform:scale(.5)}',
      // Solo en mobile: en desktop el rol lo cumplen los thumbs.
      '@media (min-width:769px){.rs-dots{display:none}}'
    ].join('');
    document.head.appendChild(st);
  };

  const currentMediaId = (gallery) => {
    const cur = gallery.querySelector('.media-viewer__item.is-current-variant');
    return cur ? cur.dataset.mediaId : null;
  };

  const filtrar = (gallery, mediaId) => {
    const viewer = gallery.querySelector('.media-viewer');
    if (!viewer) return;
    const items = Array.from(viewer.querySelectorAll('.media-viewer__item'));
    if (!items.length) return;

    // Sin mediaId (variante sin imagen propia) esto deja solo las
    // compartidas, que es el escalón b) de la escalera.
    const sobrevive = (item) =>
      !item.dataset.variantId ||
      (Boolean(mediaId) && item.dataset.mediaId === String(mediaId));

    const quedan = items.filter(sobrevive).length;
    const aplicar = quedan >= MIN_VISIBLE;

    items.forEach((item) => {
      const ocultar = aplicar && !sobrevive(item);
      item.classList.toggle(HIDDEN, ocultar);

      // El thumb de desktop no trae data-variant-id, se lo mapea por media-id.
      // Si no, en desktop se verían 54 thumbs para 20 fotos y al clickear
      // uno oculto la galería saltaría a un slide invisible.
      const thumb = gallery.querySelector(
        `.media-thumbs__item[data-media-id="${item.dataset.mediaId}"]`
      );
      if (thumb) thumb.classList.toggle(HIDDEN, ocultar);
    });

    if (typeof gallery.setVisibleItems === 'function') gallery.setVisibleItems();

    armarDots(gallery, items.filter((i) => !i.classList.contains(HIDDEN)));
  };

  /* ── Dots · ventana deslizante de 5 ─────
     Figma 6090-20030. Con 20 fotos, 20 dots son ruido, así que se ve
     siempre una ventana de 5 y el riel corre.
     Los colores del Figma (#101828 / #D1D5DC) son grises de Tailwind; van
     los cálidos equivalentes: #1A1816 (text/primary) y #C9C0B1, que el
     theme ya usa en el divisor del breadcrumb.
     aria-hidden a propósito: un dot de 6px es un target imposible, y la
     galería ya tiene swipe, flechas y el aria-live de media-gallery__status.
     Son un indicador de posición, no un control.                        */
  const VENTANA = 5;
  const PITCH = 14; // 6 de dot + 8 de gap

  const pintarDots = (gallery) => {
    const cont = gallery.rsDots;
    if (!cont) return;
    const visibles = gallery.rsVisibles || [];
    const activo = gallery.rsActivo || 0;

    cont.hidden = visibles.length < 2;
    if (cont.hidden) return;

    // La ventana se centra en el activo y se frena en los extremos.
    const desde = Math.max(0, Math.min(activo - 2, visibles.length - VENTANA));
    cont.rail.style.transform = 'translateX(' + -desde * PITCH + 'px)';

    Array.prototype.forEach.call(cont.rail.children, (dot, i) => {
      const dentro = i >= desde && i < desde + VENTANA;
      const esBordeConMas =
        (i === desde && desde > 0) ||
        (i === desde + VENTANA - 1 && desde + VENTANA < visibles.length);
      dot.classList.toggle('rs-dots__dot--on', i === activo);
      dot.classList.toggle('rs-dots__dot--edge', dentro && esBordeConMas);
    });
  };

  const armarDots = (gallery, visibles) => {
    let cont = gallery.rsDots;
    if (!cont) {
      cont = document.createElement('div');
      cont.className = 'rs-dots';
      cont.setAttribute('aria-hidden', 'true');
      const win = document.createElement('div');
      win.className = 'rs-dots__win';
      cont.rail = document.createElement('div');
      cont.rail.className = 'rs-dots__rail';
      win.appendChild(cont.rail);
      cont.appendChild(win);
      const viewer = gallery.querySelector('.media-gallery__viewer');
      if (viewer && viewer.parentNode) viewer.parentNode.insertBefore(cont, viewer.nextSibling);
      gallery.rsDots = cont;
    }
    // Un dot por foto visible; se reconstruye si cambió la cantidad.
    if (cont.rail.children.length !== visibles.length) {
      cont.rail.textContent = '';
      visibles.forEach(() => {
        const d = document.createElement('span');
        d.className = 'rs-dots__dot';
        cont.rail.appendChild(d);
      });
    }
    gallery.rsVisibles = visibles;
    // El observer mira solo las visibles del momento.
    if (gallery.rsObs) gallery.rsObs.disconnect();
    gallery.rsObs = new IntersectionObserver((entries) => {
      let mejor = null;
      entries.forEach((e) => {
        if (!mejor || e.intersectionRatio > mejor.intersectionRatio) mejor = e;
      });
      if (!mejor || mejor.intersectionRatio < 0.5) return;
      const i = gallery.rsVisibles.indexOf(mejor.target);
      if (i > -1 && i !== gallery.rsActivo) {
        gallery.rsActivo = i;
        pintarDots(gallery);
      }
    }, { root: gallery.querySelector('.media-viewer'), threshold: [0.5, 0.75, 1] });
    visibles.forEach((v) => gallery.rsObs.observe(v));

    gallery.rsActivo = Math.max(0, visibles.findIndex((v) => v.classList.contains('is-current-variant')));
    pintarDots(gallery);

    // Hook de test. El IntersectionObserver no entrega callbacks con
    // document.hidden === true, así que en una pestaña en segundo plano el
    // scroll no se puede observar. Esto permite ejercitar la matemática de
    // la ventana sin depender de que el navegador pinte.
    gallery.rsSetActivo = (i) => {
      gallery.rsActivo = Math.max(0, Math.min(i, (gallery.rsVisibles || []).length - 1));
      pintarDots(gallery);
      return gallery.rsActivo;
    };
  };

  /* ── Click en un thumb: ir directo, sin recorrer los intermedios ─────
     Medido: un click en el thumb 12 estando en el 0 disparaba 14 llamadas
     a customSetActiveMedia y 14 scrollTo({behavior:'smooth'}) sobre el
     strip. El viewer hace un scroll suave largo, handleScroll
     (media-gallery.js:444) detecta CADA índice intermedio, y cada uno
     llama a setActiveThumb → checkThumbVisibilty → un scroll nuevo que
     interrumpe al anterior. Encima el anillo de seleccionado corre por los
     13 thumbs como un ticker. Eso es la "animación rara".

     Se ataca en el origen: si el salto es de más de un lugar, se hace con
     behavior 'auto'. Sin scroll largo no hay índices intermedios. El propio
     theme ya usa 'auto' con este mismo razonamiento en el wrap del loop
     ("use 'auto' on loop wrap to avoid long smooth scrolls",
     media-gallery.js:530).

     APLICA A TODO CLICK, también al adyacente. Al principio dejé el smooth
     para saltos de un lugar, pensando que a esa distancia se veía bien. Un
     MutationObserver sobre la clase is-active mostró que no: en el salto
     12 → 13 el anillo iba al 13 a los 38ms, VOLVÍA al 12 a los 93ms y
     recién se asentaba en el 13 a los 227ms. 134ms parado en el thumb
     equivocado. Con el scroll suave arrancando, handleScroll alcanza a
     leer el índice viejo y lo reaplica.
     Se pierde el deslizamiento en el salto corto; se gana que el anillo no
     titile. Un cambio instantáneo es además lo que hacen casi todas las
     galerías al clickear una miniatura.
     Verificado: lejano 14 → 1 llamada; adyacente sin rebote.

     Va en capture sobre .media-thumbs para correr antes del listener del
     theme (media-gallery.js:330) y poder frenarlo. Se replica su llamada a
     playActiveMedia para no romper los thumbs de video o modelo 3D.

     DEUDA: es un parche en runtime sobre el comportamiento del theme. Su
     lugar es media-gallery.js, pasándole 'auto' a customSetActiveMedia
     desde handleThumbClick. Se hizo acá para no re-transcribir por la
     Admin API los 24 KB del JS central de la galería. Mover con la CLI. */
  const arreglarSaltoDeThumbs = (gallery) => {
    const strip = gallery.querySelector('.media-thumbs');
    if (!strip || gallery.rsSaltoParcheado) return;
    gallery.rsSaltoParcheado = true;

    strip.addEventListener('click', (evt) => {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;
      const item = gallery.querySelector(
        '.media-viewer__item[data-media-id="' + thumb.dataset.mediaId + '"]'
      );
      if (!item) return;

      const destino = (gallery.visibleItems || []).indexOf(item);
      if (destino < 0) return;
      // Click en el que ya está activo: que lo maneje el theme, que también
      // corta solo (customSetActiveMedia sale si el item no cambió).
      if (destino === gallery.currentIndex) return;

      evt.stopPropagation();
      gallery.customSetActiveMedia(item, true, 'auto');

      const Clase = gallery.constructor;
      if (Clase && typeof Clase.playActiveMedia === 'function') {
        Clase.playActiveMedia(item);
      }
    }, true);
  };

  const init = (gallery) => {
    if (gallery.dataset.rsFilterReady) return;
    gallery.dataset.rsFilterReady = 'true';

    filtrar(gallery, currentMediaId(gallery));
    gallery.rsRefreshVisible = () => filtrar(gallery, currentMediaId(gallery));
    arreglarSaltoDeThumbs(gallery);

    /* EN document Y EN CAPTURE, no en la sección y en bubble.
       No es preferencia: es un bug que reportó Abi (2026-09-08) — al elegir
       una variante desde los dropdowns, en MOBILE la galería no cambiaba de
       foto.

       Causa: el handler del theme (media-gallery.js:77) está registrado
       antes que el nuestro, así que corría primero. Cuando buscaba la foto
       de la variante nueva, nuestro filtro todavía tenía el estado de la
       variante ANTERIOR, donde esa foto está en display:none. Medido en el
       momento de la llamada:

         tieneClaseOculta: true · clientWidth: 0 · estaEnVisibleItems: -1

       setVisibleItems() arma la lista con clientWidth > 0, así que la foto
       no figuraba. El theme hace indexOf → -1, lo fuerza a 0
       (media-gallery.js:537) y scrollea al slide 0. Después corría nuestro
       filtro y la mostraba, pero ya nadie iba a buscarla.

       Solo en mobile porque la regla que oculta vive en
       @media (max-width:768px): en desktop nada está oculto y el indexOf
       siempre acierta.

       Un listener en document + capture corre SIEMPRE antes que cualquiera
       registrado en un descendiente, sin importar el orden de registro. Así
       el filtro se aplica primero y el theme encuentra la foto donde
       espera. Verificado.

       El guard de sección es porque document alcanza a toda la página: sin
       él, una galería filtraría por el cambio de variante de otra (quick
       add, productos relacionados). */
    const section = gallery.closest('.shopify-section');
    document.addEventListener('on:variant:change', (evt) => {
      if (section && evt.target instanceof Node && !section.contains(evt.target)) return;
      const media = evt.detail && evt.detail.variant && evt.detail.variant.featured_media;
      // Variante sin imagen propia: null deja solo las compartidas. Lo que
      // NO puede pasar es quedarse con el filtro anterior, que sería el de
      // una variante que ya no está elegida.
      filtrar(gallery, media ? media.id : null);
    }, true);
  };

  const arrancar = () => {
    inyectarEstilo();
    document.querySelectorAll('media-gallery').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
})();
